/**
 * Inspector ID field (ADR-0045) — surfaces a component's NODE ID and lets the
 * operator override it (Appsmith "widget name"). The id is autogenerated (`n1`,
 * `n2`, …) but addressable: a modal/drawer is opened by it (`openModal("<id>")`
 * / the action target picker), so it must be visible AND renameable.
 *
 * Editing is buffered in local state and committed on blur / Enter — NOT per
 * keystroke (a live rename mid-type would churn selection + fail the uniqueness
 * check on every intermediate value). `validate` mirrors `pageTree.validateNodeId`
 * so the inline hint matches what the commit will accept; `onRename` does the
 * actual tree rewrite (id + every action `target` that referenced it).
 */
import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";

const ERR: Record<string, string> = {
  empty: "L'ID non può essere vuoto",
  shape: "Solo lettere, numeri e _ (deve iniziare con una lettera)",
  duplicate: "ID già usato in questa pagina",
};

export function NodeIdField({
  id,
  validate,
  onRename,
}: {
  id: string;
  /** Returns an error code (`empty`/`shape`/`duplicate`) or null for a candidate. */
  validate: (candidate: string) => string | null;
  onRename: (newId: string) => void;
}) {
  const [draft, setDraft] = useState(id);
  const [copied, setCopied] = useState(false);
  // Re-seed when the selected node changes (or an external rename lands).
  useEffect(() => setDraft(id), [id]);

  const err = draft.trim() === id ? null : validate(draft);

  const commit = () => {
    const next = draft.trim();
    if (next === id) return; // unchanged
    if (validate(draft)) {
      setDraft(id); // invalid → revert to the live id
      return;
    }
    onRename(next);
  };

  const copy = () => {
    void navigator.clipboard?.writeText(id);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="mb-3 border-b border-border pb-3">
      <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
        ID componente
      </label>
      <div className="mt-1 flex items-center gap-1">
        <input
          aria-label="Node id"
          className={`w-full rounded border bg-background px-2 py-1 font-mono text-sm ${
            err ? "border-danger" : "border-border"
          }`}
          value={draft}
          spellCheck={false}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === "Escape") {
              setDraft(id);
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
        <button
          type="button"
          title="Copia l'ID"
          aria-label="Copia l'ID"
          onClick={copy}
          className="shrink-0 rounded border border-border p-1.5 text-muted-foreground hover:text-foreground"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
      {err ? (
        <p className="mt-1 text-[11px] leading-snug text-danger">{ERR[err] ?? err}</p>
      ) : (
        <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
          Usalo per indirizzare il componente: <code>openModal("{id}")</code>.
        </p>
      )}
    </div>
  );
}
